import React, { useState } from 'react';
import { X } from 'lucide-react';
import { galleryImages } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';
import { FadeIn, StaggerContainer } from './AnimatedComponents';

const Gallery = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const { t } = useLanguage();

  return (
    <section id="gallery" className="py-24 bg-stone-950 relative overflow-hidden">
      {/* Top Border */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-amber-700/50 to-transparent" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <FadeIn className="text-center mb-16">
          <p className="text-amber-500 uppercase tracking-[0.3em] text-sm mb-4">
            {t('gallery.subtitle')}
          </p>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-white">
            {t('gallery.title')}
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-amber-600 to-amber-400 mx-auto mt-6" />
        </FadeIn>

        {/* Gallery Grid */}
        <StaggerContainer className="grid grid-cols-2 md:grid-cols-3 gap-4" staggerDelay={100}>
          {galleryImages.map((image, index) => (
            <button
              key={index}
              onClick={() => setSelectedImage(image)}
              className={`relative group overflow-hidden rounded-2xl hover-lift ${
                index === 0 ? 'md:col-span-2 md:row-span-2' : ''
              }`}
            >
              <div className="img-zoom h-full">
                <img
                  src={image.src}
                  alt={image.alt || `Trattoria Pizzeria Da Daniele Solothurn - foto ${index + 1}`}
                  className={`w-full object-cover ${
                    index === 0 ? 'h-64 md:h-full' : 'h-48 md:h-64'
                  }`}
                  loading="lazy"
                  decoding="async"
                />
              </div>
              {/* Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-stone-950/80 via-stone-950/20 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-500" />
              {/* Zoom icon */}
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300">
                <div className="w-12 h-12 bg-amber-600/80 rounded-full flex items-center justify-center transform scale-0 group-hover:scale-100 transition-transform duration-300">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                  </svg>
                </div>
              </div>
              {image.alt && (
                <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                  <p className="text-white font-medium">{image.alt}</p>
                </div>
              )}
            </button>
          ))}
        </StaggerContainer>
      </div>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-50 bg-stone-950/95 backdrop-blur-md flex items-center justify-center p-4 animate-fade-in"
          onClick={() => setSelectedImage(null)}
        >
          <button
            onClick={() => setSelectedImage(null)}
            className="absolute top-6 right-6 text-white hover:text-amber-400 transition-all duration-300 hover:rotate-90 hover:scale-110"
          >
            <X className="w-8 h-8" />
          </button>
          <img
            src={selectedImage.src}
            alt={selectedImage.alt || 'Gallery image'}
            className="max-w-full max-h-[85vh] object-contain rounded-lg animate-scale-in"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </section>
  );
};

export default Gallery;
